import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import { getSubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';

import styles from './HomeUnsubscribedModal.module.css';

function IconAlert() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <path d="M12 9v4M12 17h.01" />
        </svg>
    );
}
function IconReceipt() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
            <path d="M14 2v5h6" />
            <path d="M8 13h8M8 17h5" />
        </svg>
    );
}
function IconRefresh() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            <path d="M3 21v-5h5" />
        </svg>
    );
}

function IconGift() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7" />
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
    );
}

/**
 * Remise de retour d'un ancien essai (-50 % sur le 1er mois), que l'email de
 * reconquête annonce et que stripe-b2b applique d'office au paiement. Sans
 * réponse du serveur : rien d'affiché, la carte reste comme avant.
 */
function useReturnOffer(clientId?: string): number | null {
    const [percentOff, setPercentOff] = useState<number | null>(null);
    useEffect(() => {
        setPercentOff(null);
        if (!clientId) return undefined;
        let cancelled = false;
        getSubscriptionStatus(clientId).then((status) => {
            const percent = status?.returnOffer?.percentOff;
            // Réactivation réservée aux admins : pas de remise montrée à un membre qui ne peut pas la prendre.
            const usable = status?.viewerRole === 'admin' && typeof percent === 'number' && percent > 0;
            if (!cancelled) setPercentOff(usable ? percent : null);
        });
        return () => {
            cancelled = true;
        };
    }, [clientId]);
    return percentOff;
}

export function HomeUnsubscribedModal({ client }: { client: any }) {
    const { t, i18n } = useTranslation();
    const returnOfferPercent = useReturnOffer(client?.id);

    return (
        // PAS `aria-modal` : le reste de la page n'est plus inerte — la barre du
        // haut (compte, factures, changement d'établissement) reste volontairement
        // utilisable. L'annoncer modal enfermerait les lecteurs d'écran dans la
        // carte, c'est-à-dire exactement le blocage qu'on vient de lever.
        <div className={styles.backdrop} role="dialog" aria-labelledby="unsubscribed-title">
            <div className={styles.card}>
                <div className={styles.iconWrap}>
                    <IconAlert />
                </div>
                <h2 className={styles.title} id="unsubscribed-title">
                    {t('account.subscriptionDeactivated')}
                </h2>
                <p className={styles.desc}>{t('account.reactivateSubscriptionInfo')}</p>
                {returnOfferPercent && (
                    <div className={styles.offer}>
                        <IconGift />
                        <div>
                            <p className={styles.offerTitle}>
                                {t('account.returnOfferTitle', { percent: returnOfferPercent })}
                            </p>
                            <p className={styles.offerNote}>{t('account.returnOfferNote')}</p>
                        </div>
                    </div>
                )}
                <div className={styles.actions}>
                    <StripeServerForm
                        className={styles.form}
                        route="/reactivate-subscription"
                        clientId={client?.id}
                        lang={i18n.language}
                    >
                        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- blocking dialog: move focus to its primary action */}
                        <button type="submit" className={styles.cta} autoFocus>
                            <IconRefresh />
                            {t('account.reactivateSubscription')}
                        </button>
                    </StripeServerForm>

                    {/* Portail Stripe = seul endroit où le client retrouve son
                        historique de factures. Sans ce bouton, un client résilié
                        n'avait aucun chemin vers ses factures depuis le produit
                        (la page Compte, qui le propose, est derrière la navbar).
                        Le portail propose aussi de régler les vieilles factures
                        restées ouvertes : des clients les ont payées en croyant
                        se réactiver (cas réel le 08/10/2026 : 2 × 49 € payés
                        sans effet), d'où le libellé « anciennes » et
                        l'avertissement. */}
                    {client?.stripeCustomerId && (
                        <>
                            <StripeServerForm
                                className={styles.form}
                                route="/create-customer-portal-session"
                                clientId={client.id}
                                lang={i18n.language}
                            >
                                <button type="submit" className={styles.secondaryCta}>
                                    <IconReceipt />
                                    {t('account.pastInvoices')}
                                </button>
                            </StripeServerForm>
                            <p className={styles.hint}>{t('account.pastInvoicesHint')}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
